import { appAxiosInstance } from '@/config/axios';
import { getStructuredUserDetailsResponse } from '@/models/user';

import {
	ISaveUserFilterPayload,
	IUpdateDefaultWorkspacePayload,
	IUpdateUserProfileImageResponse,
	IUpdateUserProfilePayload,
	IUpdateUsernamePayload,
	IUserDetailsResponse,
} from './user.types';

export async function getUserDetails() {
	const res = await appAxiosInstance.post('/user/me');

	const data = res.data as IUserDetailsResponse;

	return getStructuredUserDetailsResponse(data);
}

export async function updateUsername(payload: IUpdateUsernamePayload) {
	const res = await appAxiosInstance.post('/user/update-name', payload);

	const data = res.data as string;

	return data;
}

/**
 * Explicitly turn off the onboarding flag (idempotent). After this, `/me` returns
 * `show_onboarding: false`. Provisioning a workspace already flips the computed flag,
 * so this is mainly for an explicit dismissal path — call it when onboarding is
 * finished or dismissed. Trigger is a product decision.
 *
 * Backend contract: POST /user/turn-off-onboarding
 */
export async function turnOffOnboarding(): Promise<void> {
	await appAxiosInstance.post('/user/turn-off-onboarding');
}

export async function updateDefaultWorkspace(payload: IUpdateDefaultWorkspacePayload) {
	const res = await appAxiosInstance.post('/settings/update-default-page', payload);

	const data = res.data as string;

	return data;
}

export async function saveUserFilter(payload: ISaveUserFilterPayload) {
	const res = await appAxiosInstance.post('/user/save-filter', payload);

	const data = res.data as string;

	return data;
}

/**
 * Update user profile name.
 *
 * Backend contract:
 *   POST /user/update-profile
 *   Body: { name: string }
 *   Response: string (success message)
 */
export async function updateUserProfile(payload: IUpdateUserProfilePayload) {
	const res = await appAxiosInstance.post('/user/update-profile', payload);
	return res.data as string;
}

/**
 * Update user profile image.
 * Send multipart/form-data with field "image" (File) to upload a new image,
 * or field "remove" = "true" to remove the existing image.
 *
 * Backend contract:
 *   POST /user/update-profile-image
 *   Body: FormData — field "image": File | field "remove": "true"
 *   Response: { image_url: string }
 */
export async function updateUserProfileImage(
	payload: FormData
): Promise<IUpdateUserProfileImageResponse> {
	const res = await appAxiosInstance.post('/user/update-profile-image', payload);
	return res.data as IUpdateUserProfileImageResponse;
}

